import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { addDoc, collection, getFirestore, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../../AuthContext';
import './issueReporter.css';

export const ISSUE_TYPES = [
  { value: 'wrong_number', label: 'Wrong number' },
  { value: 'not_working', label: 'Not working' },
  { value: 'looks_wrong', label: 'Looks wrong' },
  { value: 'idea', label: 'Idea' },
] as const;

type Picked = { el: HTMLElement; rect: DOMRect };

/** A short CSS path to find the element again (tag, id, first classes, nth-of-type), up to six levels. */
function cssPath(el: Element): string {
  const parts: string[] = [];
  let cur: Element | null = el;
  while (cur && cur !== document.body && parts.length < 6) {
    let part = cur.tagName.toLowerCase();
    if (cur.id) {
      parts.unshift(`${part}#${cur.id}`);
      break;
    }
    const cls = Array.from(cur.classList).filter((c) => !/^(css-|Mui[A-Z]|Mui-)/.test(c)).slice(0, 2);
    if (cls.length) part += `.${cls.join('.')}`;
    const parent: Element | null = cur.parentElement;
    if (parent) {
      const node: Element = cur;
      const same = Array.from(parent.children).filter((c) => c.tagName === node.tagName);
      if (same.length > 1) part += `:nth-of-type(${same.indexOf(node) + 1})`;
    }
    parts.unshift(part);
    cur = parent;
  }
  return parts.join(' > ');
}

const appVersion = () => {
  const s = Array.from(document.scripts).map((x) => x.src).find((src) => /main\.[a-f0-9]+\.js/.test(src));
  return s ? (s.match(/main\.([a-f0-9]+)\.js/) || [])[1] || 'dev' : 'dev';
};

/**
 * "Something wrong here? Report it": a flag button for the top bar. Clicking it lets the person pick
 * the part of the page that is wrong, write what is wrong, and send it with the page details to
 * Firestore `issueReports`, where the owner reviews it on the Reported issues page.
 */
export default function IssueReporter({ compact = false, projectName = null }: { compact?: boolean; projectName?: string | null }) {
  const { currentUser: shownUser, actorUser, currentProjectId, roleId, viewingAs } = useAuth();
  // While "viewing as" someone, the report still comes from the real signed-in person.
  const currentUser = actorUser || shownUser;
  const [mode, setMode] = useState<'off' | 'pick' | 'write' | 'sent'>('off');
  const [hover, setHover] = useState<DOMRect | null>(null);
  const [picked, setPicked] = useState<Picked | null>(null);
  const [comment, setComment] = useState('');
  const [type, setType] = useState<string>('not_working');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const hoverEl = useRef<HTMLElement | null>(null);

  const reset = useCallback(() => {
    setMode('off');
    setHover(null);
    setPicked(null);
    setComment('');
    setType('not_working');
    setError('');
    hoverEl.current = null;
  }, []);

  // Pick mode: outline what is under the pointer; the next click picks it instead of acting on the page.
  useEffect(() => {
    if (mode !== 'pick') return;
    const inUi = (t: EventTarget | null) => t instanceof Element && !!t.closest('[data-issue-ui]');
    const onMove = (e: MouseEvent) => {
      if (inUi(e.target) || !(e.target instanceof HTMLElement)) return setHover(null);
      hoverEl.current = e.target;
      setHover(e.target.getBoundingClientRect());
    };
    const onClick = (e: MouseEvent) => {
      if (inUi(e.target)) return;
      e.preventDefault();
      e.stopPropagation();
      const el = (e.target instanceof HTMLElement ? e.target : hoverEl.current) as HTMLElement | null;
      if (!el) return;
      setPicked({ el, rect: el.getBoundingClientRect() });
      setHover(null);
      setMode('write');
    };
    const block = (e: Event) => {
      if (inUi(e.target)) return;
      e.preventDefault();
      e.stopPropagation();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') reset();
    };
    document.addEventListener('mousemove', onMove, true);
    document.addEventListener('click', onClick, true);
    document.addEventListener('mousedown', block, true);
    document.addEventListener('mouseup', block, true);
    document.addEventListener('keydown', onKey, true);
    document.body.classList.add('ir-picking');
    return () => {
      document.removeEventListener('mousemove', onMove, true);
      document.removeEventListener('click', onClick, true);
      document.removeEventListener('mousedown', block, true);
      document.removeEventListener('mouseup', block, true);
      document.removeEventListener('keydown', onKey, true);
      document.body.classList.remove('ir-picking');
    };
  }, [mode, reset]);

  useEffect(() => {
    if (mode !== 'write') return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) reset();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, mode, reset]);

  useEffect(() => {
    if (mode !== 'sent') return;
    const t = window.setTimeout(reset, 2600);
    return () => window.clearTimeout(t);
  }, [mode, reset]);

  const submit = async () => {
    if (!comment.trim()) return setError('Write what is wrong first.');
    if (!currentUser?.uid) return setError('Sign in again to send a report.');
    setBusy(true);
    setError('');
    try {
      const el = picked?.el;
      const path = window.location.pathname;
      const unitMatch = path.match(/\/units\/([^/]+)/);
      await addDoc(collection(getFirestore(), 'issueReports'), {
        comment: comment.trim().slice(0, 4000),
        type,
        status: 'new',
        page: { path, search: window.location.search || '', title: document.title || '', app: path.startsWith('/office') ? 'office' : 'crm' },
        projectId: currentProjectId || null,
        projectName: projectName || null,
        unitId: unitMatch ? decodeURIComponent(unitMatch[1]) : null,
        element: el
          ? {
              text: String(el.innerText || el.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 300),
              label: el.getAttribute('aria-label') || el.getAttribute('title') || null,
              tag: el.tagName.toLowerCase(),
              path: cssPath(el),
              rect: picked ? { x: Math.round(picked.rect.x), y: Math.round(picked.rect.y), w: Math.round(picked.rect.width), h: Math.round(picked.rect.height) } : null,
            }
          : null,
        device: {
          viewport: `${window.innerWidth}×${window.innerHeight}`,
          screen: `${window.screen?.width || 0}×${window.screen?.height || 0}`,
          userAgent: navigator.userAgent.slice(0, 300),
          touch: 'ontouchstart' in window,
        },
        appVersion: appVersion(),
        reporterUid: currentUser.uid,
        reporterEmail: currentUser.email || null,
        reporterName: currentUser.displayName || null,
        reporterRole: roleId || null,
        viewingAs: viewingAs ? { uid: viewingAs.uid, name: viewingAs.name, roleId: viewingAs.roleId } : null,
        notes: [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      setMode('sent');
    } catch (err: any) {
      console.error('Issue report failed', err);
      setError(err?.code === 'permission-denied' ? 'Reports are not switched on yet. Ask the admin to deploy the latest rules.' : "Couldn't send the report. Try again.");
    } finally {
      setBusy(false);
    }
  };

  // Comment box next to the picked element, kept inside the screen.
  const boxStyle = (() => {
    if (!picked) return {};
    const w = Math.min(340, window.innerWidth - 24);
    const below = picked.rect.bottom + 10;
    const top = below + 260 < window.innerHeight ? below : Math.max(12, picked.rect.top - 270);
    const left = Math.min(Math.max(12, picked.rect.left), window.innerWidth - w - 12);
    return { top, left, width: w };
  })();

  return (
    <>
      <button
        type="button"
        className={`ir-btn${compact ? ' compact' : ''}${mode !== 'off' ? ' on' : ''}`}
        onClick={() => (mode === 'off' ? setMode('pick') : reset())}
        aria-label="Something wrong here? Report it"
        title="Something wrong here? Report it"
        data-issue-ui
      >
        <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M5 21V4" /><path d="M5 4h11l-2 4 2 4H5" />
        </svg>
      </button>
      {mode !== 'off'
        ? createPortal(
            <div className="ir-layer" data-issue-ui>
              {mode === 'pick' ? (
                <div className="ir-banner" role="status">
                  <span>Click the part of the page that is wrong</span>
                  <button type="button" onClick={reset}>Cancel (Esc)</button>
                </div>
              ) : null}
              {mode === 'pick' && hover ? <div className="ir-outline" style={{ top: hover.top - 3, left: hover.left - 3, width: hover.width + 6, height: hover.height + 6 }} /> : null}
              {(mode === 'write' || mode === 'sent') && picked ? (
                <div className="ir-outline picked" style={{ top: picked.rect.top - 3, left: picked.rect.left - 3, width: picked.rect.width + 6, height: picked.rect.height + 6 }} />
              ) : null}
              {mode === 'write' ? (
                <div className="ir-box" style={boxStyle} role="dialog" aria-label="Report a problem">
                  <div className="ir-box-hd">
                    <b>What is wrong here?</b>
                    <button type="button" className="ir-x" onClick={reset} aria-label="Cancel"><svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18" /></svg></button>
                  </div>
                  {picked?.el ? <p className="ir-picked">“{String(picked.el.innerText || picked.el.getAttribute('aria-label') || picked.el.tagName.toLowerCase()).replace(/\s+/g, ' ').trim().slice(0, 80) || 'Picked area'}”</p> : null}
                  <div className="ir-types" role="group" aria-label="Kind of problem">
                    {ISSUE_TYPES.map((t) => (
                      <button key={t.value} type="button" aria-pressed={type === t.value} onClick={() => setType(t.value)}>{t.label}</button>
                    ))}
                  </div>
                  <label className="ir-sr" htmlFor="ir-comment">Describe the problem</label>
                  <textarea id="ir-comment" autoFocus rows={4} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="For example: balance shows ₹3 L but the buyer has paid ₹50,000 more" />
                  {error ? <p className="ir-err" role="alert">{error}</p> : null}
                  <div className="ir-actions">
                    <button type="button" className="ir-ghost" onClick={() => { setMode('pick'); setPicked(null); }}>Pick again</button>
                    <button type="button" className="ir-send" onClick={submit} disabled={busy}>{busy ? 'Sending…' : 'Send report'}</button>
                  </div>
                </div>
              ) : null}
              {mode === 'sent' ? <div className="ir-toast" role="status">Thanks, reported. We will look into it.</div> : null}
            </div>,
            document.body
          )
        : null}
    </>
  );
}
